/**
 * Subscription Status Hook
 *
 * Fetches the workspace's subscription status from Central.
 * Used by the Credits tab to show plan context alongside balance.
 */

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { SubscriptionInfo } from '@levelup/central-billing';
import { isActiveSubscription } from '@levelup/central-billing';
import type { RootState } from '../store';
import { fetchSubscriptionStatus } from '../lib/centralBilling';
import { usePermissions } from './usePermissions';

export interface UseSubscriptionStatusResult {
  subscription: SubscriptionInfo | null;
  isActive: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useSubscriptionStatus(): UseSubscriptionStatusResult {
  const workspaceId = useSelector(
    (state: RootState) => state.boot.context?.currentWorkspace?.id ?? null,
  );
  const { hasWorkspacePermission } = usePermissions();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['subscriptionStatus', workspaceId],
    queryFn: () => fetchSubscriptionStatus(workspaceId!),
    enabled: !!workspaceId && hasWorkspacePermission('manageBilling'),
    staleTime: 60000,
    retry: 1,
  });

  const subscription = data?.subscription ?? null;

  return {
    subscription,
    isActive: subscription ? isActiveSubscription(subscription.status) : false,
    isLoading,
    isError,
  };
}
